// @flow

import * as React from 'react';

import clsx from 'clsx';
import isEmpty from 'lodash/isEmpty';

import { CircleAlert } from 'lucide-react';

import { useLocation, useMatch } from 'react-router-dom';

import { Icon, Text, View } from 'reshaped';

import {
  selectCartTotal,
  selectCartCurrency,
  selectContracts,
  selectProductErrors,
} from '@gandi/cart';

import { Alert } from '@gandi/reshaped';

import { useTranslator } from '@gandi/translate';

import { useAppSelector } from 'src/hooks/useAppSelector';

import { selectIsAppWorking } from 'src/modules/app/selectors';

import { useCartSummaryContext } from '../../../context/CartSummary';

import { CartSummaryPriceBreakdown } from './CartSummaryPriceBreakdown';
import { CheckoutButton } from './CheckoutButton';
import { ContactFormSubmitButton } from './ContactFormSubmitButton';
import { ContinueButton } from './ContinueButton';

import { PayLaterButton } from './PayLaterButton';

import styles from './CartSummaryButton.module.css';

type Props = {
  scrollBottom: boolean,
};

type RefType = HTMLDivElement;

function Button({ price }: { price: string }) {
  const location = useLocation();
  const overviewPage = location.pathname.includes('overview');
  const studioPage = Boolean(useMatch('/studio/:cartId/account'));
  const accountFormPage =
    location.pathname.includes('account/create') || studioPage;

  switch (true) {
    case overviewPage:
      return <CheckoutButton price={price} />;
    case accountFormPage:
      return <ContactFormSubmitButton price={price} />;
    default:
      return <ContinueButton price={price} />;
  }
}

function CartSummaryButton(
  { scrollBottom }: Props,
  ref: React.RefSetter<RefType | null>,
): React.MixedElement | null {
  const translator = useTranslator();

  const { __, formatPrice } = translator;

  const { cartOwnerOrganization } = useCartSummaryContext();

  const location = useLocation();
  const overviewPage = location.pathname.includes('overview');
  const checkoutPage = location.pathname.includes('checkout');
  const generateProformaPage = location.pathname.includes('generate-proforma');

  const currency = useAppSelector(selectCartCurrency);
  const total = useAppSelector(selectCartTotal);

  const contracts = useAppSelector(selectContracts);
  const allContractsApproved = contracts.every(contract => contract.isApproved);

  const errors = useAppSelector(selectProductErrors);
  const hasErrors = errors?.length > 0;

  const isAppWorking = useAppSelector(selectIsAppWorking);

  if (isEmpty(total) || !currency) return null;
  const { afterTaxes } = total;

  const price = formatPrice(afterTaxes, currency);

  const isIndividualOrga = cartOwnerOrganization?.type === 'individual';
  return (
    <div
      ref={ref}
      id="cartSummaryButton"
      className={clsx(
        styles.container,
        scrollBottom ? styles.scrollBottom : undefined,
      )}
    >
      {overviewPage &&
        (hasErrors ? (
          <View width="100%">
            <Alert icon={CircleAlert} color="critical">
              <p>
                {' '}
                {__(
                  '<for-translators-use>%(count)s</for-translators-use>Some products in your cart require your attention.',
                  { count: errors.length },
                )}
              </p>
            </Alert>
          </View>
        ) : (
          !allContractsApproved && (
            <View
              width="100%"
              direction="row"
              gap={3}
              align="center"
              justify="center"
            >
              <Icon svg={CircleAlert} color="warning" size={5} />

              <Text as="p" variant="body-3" color="warning" weight="medium">
                {__(
                  '<for-translators-use>%(count)s</for-translators-use>Please accept all contracts above to proceed.',
                  {
                    count: contracts.length,
                  },
                )}
              </Text>
            </View>
          )
        ))}
      {!checkoutPage && !generateProformaPage ? (
        <Button price={price} />
      ) : (
        <CartSummaryPriceBreakdown />
      )}
      {overviewPage &&
        !hasErrors &&
        allContractsApproved &&
        !isAppWorking &&
        !isIndividualOrga && <PayLaterButton />}
    </div>
  );
}

const CartSummaryButtonForwarded: React.ComponentType<Props> = React.forwardRef<
  Props,
  RefType | null,
>(CartSummaryButton);

export default CartSummaryButtonForwarded;
